/** Converts the structured filter tree to the legacy backend query syntax. */
import type { FilterGroup, FilterRule } from '../../models/filter';

function quote(value: string): string {
  const normalized = value.replaceAll("'", "''");
  return Number.isFinite(Number(value)) && value.trim() !== '' ? ` ${Number(value)}` : ` N'${normalized}'`;
}

function select(rule: FilterRule): string {
  const logical = rule.LogicalOperator === 'AND' ? ' AND ' : ' UNION SELECT ';
  if (rule.SelectType === 'count') {
    if (rule.LogicalOperator === 'AND') {
      return `\r\n AND SubmittedFormID IN (SELECT r.SubmittedFormID FROM dbo.DF_SubmittedFormsRecords r WHERE parent =${quote(rule.Model)} GROUP BY r.SubmittedFormID, r.Model HAVING COUNT(*) ${rule.CompareOperator}${quote(rule.Value)})`;
    }
    return `\r\n UNION SELECT SubmittedFormID FROM dbo.DF_SubmittedFormsRecords WHERE parent =${quote(rule.Model)} GROUP BY SubmittedFormID, Model HAVING COUNT(*) ${rule.CompareOperator}${quote(rule.Value)}`;
  }
  const valueExpression = Number.isFinite(Number(rule.Value)) && rule.Value.trim() !== '' ? 'cast(value as float)' : 'value';
  if (rule.LogicalOperator === 'AND') {
    return `\r\n AND SubmittedFormID IN (SELECT SubmittedFormID FROM DF_SubmittedFormsRecords WHERE Model =${quote(rule.Model)} AND ${valueExpression}${rule.CompareOperator}${quote(rule.Value)})`;
  }
  return `${logical}SubmittedFormID FROM DF_SubmittedFormsRecords WHERE Model =${quote(rule.Model)} AND ${valueExpression}${rule.CompareOperator}${quote(rule.Value)}`;
}

/**
 * Produces the same SQL fragment understood by the legacy backend. Values are escaped,
 * while the backend migration can later replace this with structured predicates.
 */
export function buildLegacyFilterQuery(groups: FilterGroup[]): string {
  const byParent = new Map<string | null, FilterGroup[]>();
  for (const group of groups) {
    const key = group.parent == null ? null : String(group.parent);
    const list = byParent.get(key) ?? [];
    list.push(group);
    byParent.set(key, list);
  }

  const renderGroup = (group: FilterGroup, level: number): string => {
    let output = group.filters.map(select).join('');
    const children = byParent.get(String(group.id)) ?? [];
    for (const child of children) output += renderGroup(child, level + 1);
    // Legacy controller supported up to three levels. Each AND predicate is already closed
    // in this implementation, which avoids the malformed-parenthesis bug in the old code.
    return output;
  };

  return (byParent.get(null) ?? []).map((group) => renderGroup(group, 0)).join('');
}
